import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";
import { MAIN_SITE_URL, categories } from "@/data/categories";
import LanguageSwitcher from "./LanguageSwitcher";

const NAV_LINKS = [
  { key: "nav.home", to: "", icon: "ri-home-4-line" },
  { key: "nav.guide", to: "basetao-spreadsheet" },
  { key: "nav.articles", hash: "articles" },
  { key: "nav.review", hash: "categories" },
  { key: "nav.shipping", hash: "faq" },
];

interface NavbarProps {
  variant?: "transparent" | "solid";
}

export default function Navbar({ variant = "transparent" }: NavbarProps) {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = variant === "solid" || scrolled || menuOpen;
  const light = !solid;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid
          ? "border-b border-background-200 bg-background-50/95 backdrop-blur"
          : "bg-transparent"
      }`}
    >
      <nav className="flex h-16 items-center justify-between gap-4 px-4 md:px-6">
        <Link
          to={`/${currentLang}`}
          className="flex items-center gap-2 whitespace-nowrap"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-grid-fill text-base"></i>
          </span>
          <span
            className={`text-base font-bold font-heading ${
              light ? "text-background-50" : "text-foreground-950"
            }`}
          >
            {t("brand.name")}
          </span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) => {
            const href = link.to
              ? `/${currentLang}/${link.to}`
              : `/${currentLang}${link.hash ? `#${link.hash}` : ""}`;
            return (
              <Link
                key={link.key}
                to={href}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold whitespace-nowrap cursor-pointer transition ${
                  light
                    ? "text-background-50 hover:bg-background-50/20"
                    : "text-foreground-800 hover:bg-background-100 hover:text-foreground-950"
                }`}
              >
                {link.icon && <i className={`${link.icon} text-base`}></i>}
                {t(link.key)}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer transition hover:bg-primary-600 sm:inline-flex"
          >
            {t("cta.buyNow")}
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className={`flex h-9 w-9 items-center justify-center rounded-lg border ${
              light
                ? "border-background-50/40 text-background-50"
                : "border-background-300/60 text-foreground-900"
            } lg:hidden`}
            aria-label="Menu"
          >
            <i className={`text-lg ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => {
              const href = link.to
                ? `/${currentLang}/${link.to}`
                : `/${currentLang}${link.hash ? `#${link.hash}` : ""}`;
              return (
                <Link
                  key={link.key}
                  to={href}
                  onClick={() => setMenuOpen(false)}
                  className="inline-flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 transition hover:bg-background-100"
                >
                  {link.icon && <i className={`${link.icon} text-base`}></i>}
                  {t(link.key)}
                </Link>
              );
            })}
          </div>

          <div className="mt-4 border-t border-background-200 pt-4">
            <p className="px-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              {t("categories.title")}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-700 transition hover:border-primary-300 hover:text-foreground-950"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md">
                    <img
                      src={cat.image}
                      alt={t(`cat.${cat.key}`)}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <span className="truncate">{t(`cat.${cat.key}`)}</span>
                </a>
              ))}
            </div>
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50"
          >
            {t("cta.buyNow")}
          </a>
        </div>
      )}
    </header>
  );
}